import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable} from 'rxjs';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';
import {ElasticSearchRequest} from '@models/elastic-search/request/elastic-search-request';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {ElasticSearchFilterValue} from '@models/elastic-search/request/elastic-search-filter-value';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {ELASTIC_SEARCH_FIELDS} from '@constants/elastic-search/elastic-search-fields.constant';
import {ELASTIC_SEARCH_RETURNED_FIELDS} from '@constants/elastic-search/elastic-search-returned-fields.constant';
import {ELASTIC_SEARCH_SORT_ORDER} from '@models/elastic-search/request/elastic-search-sort-order.enum';
import {ELASTIC_SEARCH_CONSTANTS} from '@constants/elastic-search/elastic-search-constants.constant';

@Injectable({
  providedIn: 'root',
})
export class MetadataElasticSearchService {
  private readonly PARENT_ID_FIELD: string = 'parentId';
  private readonly KEY_FIELD: string = 'key';

  private ELASTIC_API_PREFIX = '/api/elastic/';
  private ITEMS_COUNT_SUFFIX = '/count';

  private readonly BASE_URL: string;

  constructor(
      private http: HttpClient,
      private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public isInvalidSearchTerm(searchTerm: string): boolean {
    if (searchTerm === '') {
      return false;
    }

    const regexPattern = new RegExp('^[' + ELASTIC_SEARCH_CONSTANTS.SPECIAL_CHARACTERS_PATTERN.join('') + ']+$');
    return regexPattern.test(searchTerm);
  }

  public countMetaData(parentId: number | string | null, type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA): Observable<any> {
    if (this.isInvalidParentId(parentId, type)) {
      throw new Error(`Invalid parentId: ${parentId}`);
    }

    const countRequest: ElasticSearchRequest = new ElasticSearchRequest();

    countRequest.filter = [new ElasticSearchFilterValue(this.PARENT_ID_FIELD, String(parentId), true)];
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + this.ITEMS_COUNT_SUFFIX;
    return this.http.post(url, countRequest, HTTPS_REQUEST_OPTIONS);
  }

  public listAllMachineMetaData(parentId: number | null): Observable<any> {
    const sortValue: ElasticSearchSortValue = new ElasticSearchSortValue(this.KEY_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC, false);
    const pageNumber: number = 0;
    const limit: number = 9999;
    return this.listMetaData('', sortValue, pageNumber, limit, parentId, ELASTIC_SEARCH_TYPE.MACHINE_META_DATA);
  }

  public listByKey(key: string, parentId: number | string | null, type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA): Observable<any> {
    if (this.isInvalidParentId(parentId, type)) {
      throw new Error(`Invalid parentId: ${parentId}`);
    }

    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.META_DATA_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.META_DATA_RETURNED_FIELDS;
    const request = new ElasticSearchRequest;
    request.fieldsToSearch = fieldsToSearch;
    request.fieldsToReturn = fieldsToReturn;
    request.searchText = '*';
    request.from = 0;
    request.size = 9999;
    request.sort = new ElasticSearchSortValue(this.KEY_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC, false);

    request.filter = [
      new ElasticSearchFilterValue(this.PARENT_ID_FIELD, String(parentId), true),
      new ElasticSearchFilterValue(this.KEY_FIELD, String(key), true)
    ];
    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);

  }

  public listMetaData(searchText: string, sortValue: ElasticSearchSortValue, pageNumber: number, limit: number, parentId: number | string | null, type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA): Observable<any> {

    if (this.isInvalidParentId(parentId, type)) {
      throw new Error(`Invalid parentId: ${parentId}`);
    }

    const fieldsToSearch: string[] = ELASTIC_SEARCH_FIELDS.META_DATA_SEARCH_FIELDS;
    const fieldsToReturn: string[] = ELASTIC_SEARCH_RETURNED_FIELDS.META_DATA_RETURNED_FIELDS;

    const page = pageNumber <= 1 ? 0 : pageNumber - 1;
    const request = new ElasticSearchRequest;
    request.fieldsToSearch = fieldsToSearch;
    request.fieldsToReturn = fieldsToReturn;
    request.searchText = this.generateSearchText(searchText);
    request.from = page * limit;
    request.size = limit;

    if (sortValue.order !== ELASTIC_SEARCH_SORT_ORDER.NONE) {
      request.sort = sortValue;
    } else {
      request.sort = new ElasticSearchSortValue(this.KEY_FIELD, ELASTIC_SEARCH_SORT_ORDER.ASC, false);
    }

    request.filter = [new ElasticSearchFilterValue(this.PARENT_ID_FIELD, String(parentId), true)];

    const url: string = this.BASE_URL + this.ELASTIC_API_PREFIX + type + '/';
    return this.http.post(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private generateSearchText(searchedValue: string): string {
    if (!searchedValue || searchedValue.trim() === '') {
      return '*';
    }

    const regexPattern = new RegExp(ELASTIC_SEARCH_CONSTANTS.SPECIAL_CHARACTERS_PATTERN.join('|'), 'g');
    const keywords = searchedValue.trim().split(regexPattern);
    const isQuotedValue = searchedValue.startsWith('"') && searchedValue.endsWith('"');

    if (isQuotedValue && keywords.length > 1) {
      const sanitizedKeywords = keywords.map((keyword: string) => keyword.trim()).filter(Boolean);
      return sanitizedKeywords.join(` ${ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN} `);
    }

    if (keywords.length > 1) {
      const sanitizedKeywords = keywords.map((keyword: string) => keyword.trim() !== '' ? `*${keyword.trim()}*` : '');
      return sanitizedKeywords.filter(Boolean).join(` ${ELASTIC_SEARCH_CONSTANTS.SEARCH_ITEMS_JOIN} `);
    }

    return `*${keywords[0].trim()}*`;
  }


  private isInvalidParentId(parentId: number | string | null, type: ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA | ELASTIC_SEARCH_TYPE.MACHINE_META_DATA): boolean {

    if (type === ELASTIC_SEARCH_TYPE.MACHINE_META_DATA) {
      return typeof parentId !== 'number' || parentId <= 0;
    }

    if (type === ELASTIC_SEARCH_TYPE.GATEWAY_META_DATA) {
      return typeof parentId !== 'string' || parentId.trim().length === 0;
    }

    return true;
  }
}
